import type { CounterView, PendingRequestView } from './types'

/**
 * The branch's undecided desk requests, kept live.
 *
 * A snapshot carries the whole list (engine.Snapshot.Requests); a delta carries
 * none of its own — only each changed desk's own CounterView.request (spec
 * section 1, "Live views"). So Floor's list is rebuilt from the deltas that
 * actually arrive: a desk whose request just became pending is added or
 * replaced, and one whose request was decided, cancelled, or is simply absent
 * now drops out. A desk nobody touched keeps the entry it already had.
 *
 * Keyed by counter rather than by request id, because the one thing the plan
 * guarantees is at most one open request per desk — never two ids to choose
 * between for the same counter.
 */
export function mergePendingRequests(
  current: readonly PendingRequestView[],
  changed: readonly CounterView[],
): PendingRequestView[] {
  const byCounter = new Map(current.map((r) => [r.counterId, r]))
  for (const desk of changed) {
    const req = desk.request
    if (req && req.state === 'pending') {
      byCounter.set(desk.counterId, {
        id: req.id,
        counterId: desk.counterId,
        counterCode: desk.code,
        staffId: desk.staffId ?? '',
        staffName: desk.staffName ?? '',
        kind: req.kind,
        requestedAt: req.requestedAt,
      })
    } else {
      byCounter.delete(desk.counterId)
    }
  }
  return [...byCounter.values()].sort((a, b) => Date.parse(a.requestedAt) - Date.parse(b.requestedAt))
}

/**
 * How many undecided requests a banner should say, for whoever may decide them.
 *
 * Nobody without status.approve is shown a number they cannot act on — the same
 * reasoning the VIP call button is offered by (lib/deskStatus.offersOutOfTurnCall):
 * a count with nowhere to go is not information, it is a tease. The viewer's
 * own request is left out for the same reason: nobody decides their own.
 */
export function requestBannerCount(
  requests: readonly PendingRequestView[] | undefined,
  permissions: readonly string[],
  viewerStaffId?: string,
): number {
  if (!permissions.includes('status.approve')) return 0
  return (requests ?? []).filter((r) => !viewerStaffId || r.staffId !== viewerStaffId).length
}
